import { useMutation, useQueryClient } from 'react-query'
import { postJSON } from 'common/services/api'
import { showToast } from 'common/utils'

type UseRiskAssessmentProps = {
  riskAssessmentId: string | undefined
  residentId?: string
  onArchiveSuccess?: () => void
}

type ArchiveMutationBody = {
  reason: string
}

const useRiskAssessmentArchive = ({
  riskAssessmentId,
  residentId,
  onArchiveSuccess,
}: UseRiskAssessmentProps) => {
  const queryClient = useQueryClient()

  const handleError = (err: unknown) => {
    showToast({
      title: err as string,
      type: 'error',
    })
  }

  const archiveFunction = (values: ArchiveMutationBody) =>
    postJSON({
      url: `list/risk-assessments/${riskAssessmentId}/archive`,
      method: 'POST',
      json: values,
    })

  const updateRiskAssessmentQueries = () => {
    queryClient.invalidateQueries(['risk-assessment', riskAssessmentId])
    queryClient.invalidateQueries(['risk-assessments'])
    queryClient.invalidateQueries(['resident', residentId, 'risk-assessments'])
    queryClient.invalidateQueries(['list-risk-assessments'])
  }

  const { mutateAsync: archiveMutation, isLoading: isArchiving } = useMutation<
    any,
    unknown,
    ArchiveMutationBody
  >(['risk-assessment', riskAssessmentId, 'archive'], archiveFunction, {
    onSuccess: () => {
      showToast({
        title: 'Risk assessment archived successfully',
        type: 'success',
      })
      onArchiveSuccess?.()
      updateRiskAssessmentQueries()
    },
    onError: (err) => handleError(err),
  })

  return {
    archiveRiskAssessment: archiveMutation,
    isArchiving,
  }
}

export default useRiskAssessmentArchive
